// The consent screen: an outside AI (a Claude connector, Claude Code, any MCP client) asks to reach Bloom as you.
// Bloom's OAuth authorization endpoint is this page (/oauth/authorize?client_id…). It shows who's asking and where
// you'll be sent back, lets you pick servers, apps and read vs act, then posts the answer to /api/connector/authorize
// and follows the redirect it gets back (the code, or "access_denied"). See docs/connector.md.
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import type { AuthorizePreview, GrantServerScope } from "@bloom/shared";
import { api } from "../api.ts";
import { useSession } from "../session.tsx";
import { BloomMark, colorFor, initials } from "../components/ui.tsx";
import { initialScopes, ScopePicker, scopesIncomplete } from "../components/ConnectorScopes.tsx";
import "../components/connector.css";

export function Authorize() {
  const { me } = useSession();
  const location = useLocation();
  const query = useMemo(() => Object.fromEntries(new URLSearchParams(location.search)), [location.search]);
  const [preview, setPreview] = useState<AuthorizePreview | null>(null);
  const [problem, setProblem] = useState<{ message: string; redirect?: string } | null>(null);
  const [scopes, setScopes] = useState<GrantServerScope[]>([]);
  const [busy, setBusy] = useState<"allow" | "deny" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!me) return;
    void (async () => {
      const res = await fetch(`/api/connector/authorize${location.search}`, { credentials: "same-origin" }).catch(() => null);
      const body = (await res?.json().catch(() => null)) as (AuthorizePreview & { message?: string; redirect?: string }) | null;
      if (!res?.ok || !body) return setProblem({ message: body?.message ?? "Couldn't load this request.", ...(body?.redirect ? { redirect: body.redirect } : {}) });
      setPreview(body);
      setScopes(initialScopes(body.servers, body.current));
    })();
  }, [me, location.search]);

  if (me === undefined) return null;
  const here = `${location.pathname}${location.search}`;
  if (me === null) {
    return (
      <Shell>
        <h1 className="ca-title">Sign in to connect</h1>
        <p className="ca-lede">An app wants to reach Bloom as you. Sign in first, then you'll choose what it may do.</p>
        <Link className="btn btn-primary" to={`/login?next=${encodeURIComponent(here)}`}>Sign in</Link>
      </Shell>
    );
  }
  if (problem) {
    return (
      <Shell>
        <h1 className="ca-title">This connection can't continue</h1>
        <p className="error" role="alert">{problem.message}</p>
        <p className="ca-fine">Nothing was shared.{problem.redirect ? "" : " You can close this page and try connecting again from the app."}</p>
        {problem.redirect && <a className="btn" href={problem.redirect}>Back to the app</a>}
      </Shell>
    );
  }
  if (!preview) {
    return (
      <Shell>
        <p className="muted">Loading…</p>
      </Shell>
    );
  }

  const answer = async (decision: "allow" | "deny") => {
    setBusy(decision);
    setError(null);
    try {
      const r = await api<{ redirect: string }>("/connector/authorize", { body: { query, decision, servers: scopes } });
      window.location.assign(r.redirect);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
      setBusy(null);
    }
  };
  const c = preview.client;
  const acting = scopes.some((s) => s.access === "act");
  return (
    <Shell>
      <div className="ca-pair" aria-hidden="true">
        <span className="ca-client" style={{ background: colorFor(c.id) }}>{initials(c.name)}</span>
        <span className="ca-link" />
        <span className="ca-bloom"><BloomMark size={26} /></span>
      </div>
      <h1 className="ca-title">
        <span className="ca-name">{c.name}</span> wants to reach Bloom as you
      </h1>
      <p className="ca-lede">
        It will see and use Bloom's apps as <strong>{me.name}</strong>, with only what you allow here and never more than you can do yourself. It reads your
        messages and DMs only if you choose that below, and never sends in DMs.
      </p>
      <dl className="ca-facts">
        <div>
          <dt>Sends you back to</dt>
          <dd className="ca-host">{c.host}</dd>
        </div>
        <div>
          <dt>Name</dt>
          <dd>{c.kind === "cimd" ? `Published by ${c.host}` : "Chosen by the app itself"}</dd>
        </div>
      </dl>
      {c.localhost && (
        <p className="ca-warn" role="note">
          This app runs on a computer (localhost). Only allow it if you started connecting it yourself just now.
        </p>
      )}
      <div className="ca-section">
        <div className="ca-section-head">
          <h2>Choose servers</h2>
          {preview.wantsAct && <span className="ca-ask">It asks to act</span>}
        </div>
        <ScopePicker servers={preview.servers} value={scopes} onChange={setScopes} idPrefix="ca" />
      </div>
      {acting && <p className="ca-fine">Anything it changes shows in the app's History as “{c.name} on behalf of {me.name}”. Deleting things always waits for your tap in Bloom.</p>}
      {error && <p className="error" role="alert">{error}</p>}
      <div className="ca-actions">
        <button type="button" className="btn" onClick={() => void answer("deny")} disabled={!!busy}>
          {busy === "deny" ? "Cancelling…" : "Cancel"}
        </button>
        <button type="button" className="btn btn-primary" onClick={() => void answer("allow")} disabled={!!busy || !scopes.length || scopesIncomplete(scopes)}>
          {busy === "allow" ? "Allowing…" : scopes.length ? `Allow ${scopes.length === 1 ? "1 server" : `${scopes.length} servers`}` : "Pick a server"}
        </button>
      </div>
      <p className="ca-fine">Change or revoke this any time in Settings → Connected apps &amp; AI.</p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="auth-page ca-page">
      <div className="auth-card ca-card">
        <div className="brand">
          <BloomMark /> Bloom
        </div>
        {children}
      </div>
    </main>
  );
}
